import { type FC, useState, useMemo, useEffect, useRef } from "react";
import { Check, ChevronRight, ChevronDown, Plus } from "lucide-react";
import { format, parseISO } from "date-fns";

import { cn } from "@/shared/utils/core";
import { formatTransactionAmount } from "@/shared/utils/formatters";
import { Tag } from "@/shared/components/ui/tag";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/shared/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { getCategoryConfig } from "@/pages/postloss/estate-inventory/constants/transactionCategories";

import { ActivityEmptyState } from "./ActivityEmptyState";
import { CategoryPicker } from "./CategoryPicker";

type ActivityFilter = "all" | "money-in" | "money-out" | "category-missing" | "receipt-missing";

const FILTER_LABELS: Record<ActivityFilter, string> = {
  "all": "All activity",
  "money-in": "Money in",
  "money-out": "Money out",
  "category-missing": "Category missing",
  "receipt-missing": "Receipt missing",
};

export interface Transaction {
  id: number;
  date: string;
  description: string;
  originalDescription?: string | null;
  payee?: string;
  category?: string;
  hasReceipt: boolean;
  amount: number;
  isInflow: boolean;
}

interface ActivityTableProps {
  transactions: Transaction[];
  onRowClick?: (transaction: Transaction) => void;
  onCategoryChange?: (transactionId: number, category: string) => void;
}

const GRID_COLS = "grid-cols-[140px_minmax(0,1fr)_200px_81px_170px_50px]";

const CategoryCell: FC<{
  transaction: Transaction;
  onCategoryChange?: (transactionId: number, category: string) => void;
}> = ({ transaction, onCategoryChange }) => {
  const config = getCategoryConfig(transaction.category, transaction.isInflow, transaction.description, transaction.originalDescription);
  if (config) {
    const tag = (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Tag color={config.color} size="sm">
                {config.label}
              </Tag>
            </div>
          </TooltipTrigger>
          <TooltipContent>{config.tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
    if (onCategoryChange) {
      return (
        <CategoryPicker isInflow={transaction.isInflow} onSelect={(cat) => onCategoryChange(transaction.id, cat)}>
          {tag}
        </CategoryPicker>
      );
    }
    return tag;
  }
  if (onCategoryChange) {
    return (
      <CategoryPicker isInflow={transaction.isInflow} onSelect={(cat) => onCategoryChange(transaction.id, cat)} />
    );
  }
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-primary text-xs text-secondary"
    >
      <Plus className="h-3 w-3" />
      Add category
    </button>
  );
};

const AmountDisplay: FC<{ amount: number; isInflow: boolean }> = ({ amount, isInflow }) => (
  <span className={cn("shrink-0 text-sm font-semibold whitespace-nowrap", isInflow ? "text-brand" : "text-primary")}>
    {formatTransactionAmount(amount, isInflow)}
  </span>
);

const PAGE_SIZE = 25;

export const ActivityTable: FC<ActivityTableProps> = ({
  transactions,
  onRowClick,
  onCategoryChange,
}) => {
  const [filter, setFilter] = useState<ActivityFilter>("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    if (filter === "all") return transactions;
    if (filter === "money-in") return transactions.filter((t) => t.isInflow);
    if (filter === "money-out") return transactions.filter((t) => !t.isInflow);
    if (filter === "category-missing") {
      return transactions.filter((t) => !getCategoryConfig(t.category, t.isInflow, t.description, t.originalDescription));
    }
    if (filter === "receipt-missing") return transactions.filter((t) => !t.hasReceipt);
    return transactions;
  }, [transactions, filter]);

  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const hasMore = visibleCount < filtered.length;

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filter]);

  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;

    let active = false;
    const timer = setTimeout(() => { active = true; }, 100);

    const observer = new IntersectionObserver(
      (entries) => {
        if (active && entries[0].isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filtered.length));
        }
      },
    );
    observer.observe(el);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [hasMore, filtered.length]);

  return (
    <div className="bg-white border border-primary rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-4 sm:px-6">
        <p className="text-base font-semibold">Activity</p>
        {transactions.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex h-10 w-[200px] items-center justify-between rounded-sm border border-primary bg-white px-3 text-sm"
              >
                <span>{FILTER_LABELS[filter]}</span>
                <ChevronDown className="h-3 w-3 text-secondary" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              {(Object.keys(FILTER_LABELS) as ActivityFilter[]).map((key) => (
                <DropdownMenuItem key={key} onSelect={() => setFilter(key)}>
                  {FILTER_LABELS[key]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      {visible.length === 0 ? (
        <ActivityEmptyState />
      ) : (
        <>
          {/* Mobile: card list */}
          <div className="sm:hidden">
            {visible.map((transaction) => (
              <div
                key={transaction.id}
                onClick={onRowClick ? () => onRowClick(transaction) : undefined}
                className={cn(
                  "flex items-center gap-3 border-t border-primary px-4 py-3",
                  onRowClick && "cursor-pointer active:bg-subtle",
                )}
              >
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-primary truncate">{transaction.description}</p>
                    <AmountDisplay amount={transaction.amount} isInflow={transaction.isInflow} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-secondary">{format(parseISO(transaction.date), "MMM d, yyyy")}</span>
                    <CategoryCell transaction={transaction} onCategoryChange={onCategoryChange} />
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-secondary" />
              </div>
            ))}
          </div>

          {/* Desktop: grid table */}
          <div className="hidden sm:block overflow-auto">
            <div className={cn("grid min-w-[700px]", GRID_COLS)}>
              <div className="contents">
                <div className="border-b border-primary px-6 py-3 text-sm font-medium text-secondary">Date</div>
                <div className="border-b border-primary px-4 py-3 text-sm font-medium text-secondary">Description</div>
                <div className="border-b border-primary px-4 py-3 text-sm font-medium text-secondary">Category</div>
                <div className="border-b border-primary px-4 py-3 text-sm font-medium text-secondary">Receipt</div>
                <div className="border-b border-primary px-4 py-3 text-sm font-medium text-secondary text-right">Amount</div>
                <div className="border-b border-primary px-4 py-3" />
              </div>

              {visible.map((transaction) => (
                <div
                  key={transaction.id}
                  onClick={onRowClick ? () => onRowClick(transaction) : undefined}
                  className={cn("contents group", onRowClick && "cursor-pointer")}
                >
                  <div className={cn("border-b border-primary px-6 py-4 text-sm text-secondary whitespace-nowrap", onRowClick && "group-hover:bg-subtle")}>
                    {format(parseISO(transaction.date), "MMM d, yyyy")}
                  </div>
                  <div className={cn("border-b border-primary px-4 py-4", onRowClick && "group-hover:bg-subtle")}>
                    <p className="text-sm font-medium text-primary">{transaction.description}</p>
                    {transaction.payee ? <p className="text-sm text-secondary">{transaction.payee}</p> : null}
                  </div>
                  <div className={cn("border-b border-primary px-4 py-4 whitespace-nowrap flex items-center", onRowClick && "group-hover:bg-subtle")}>
                    <CategoryCell transaction={transaction} onCategoryChange={onCategoryChange} />
                  </div>
                  <div className={cn("border-b border-primary px-4 py-4 whitespace-nowrap flex items-center", onRowClick && "group-hover:bg-subtle")}>
                    {transaction.hasReceipt ? (
                      <Check className="h-4 w-4 text-brand" strokeWidth={2.5} role="img" aria-label="Receipt attached" />
                    ) : null}
                  </div>
                  <div className={cn("border-b border-primary px-4 py-4 whitespace-nowrap text-right flex items-center justify-end", onRowClick && "group-hover:bg-subtle")}>
                    <AmountDisplay amount={transaction.amount} isInflow={transaction.isInflow} />
                  </div>
                  <div className={cn("border-b border-primary px-4 py-4 flex items-center justify-end", onRowClick && "group-hover:bg-subtle")}>
                    <ChevronRight className="h-4 w-4 text-secondary" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {hasMore ? <div ref={sentinelRef} className="h-px" /> : null}
    </div>
  );
};
